import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

import { Plus, Minus, RotateCcw, RotateCw } from 'lucide-react';
import { cn } from 'fc/lib/utils';
import { PdfHeaderProps, PdfViewerProps } from './pdfviewer';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/legacy/build/pdf.worker.min.mjs`;

const PdfHeader: React.FC<PdfHeaderProps> = ({
  numPages,
  currentPage,
  scale,
  zoomIn,
  zoomOut,
  goToNextPage,
  goToPreviousPage,
  rotateLeft,
  rotateRight,
  isPdf,
}) => {
  return (
    <div
      className={cn(
        'flex items-center justify-between px-4 py-2 bg-gray-900 text-white'
      )}
    >
      <div className="flex items-center space-x-4">
        <Minus
          onClick={zoomOut}
          className="text-white cursor-pointer"
          size={16}
        />
        <div className="bg-gray-700 rounded px-2 py-1">
          {(scale * 100).toFixed(0)}%
        </div>
        <Plus
          onClick={zoomIn}
          className="text-white cursor-pointer"
          size={16}
        />
      </div>
      {!isPdf && (
        <div className="flex items-center space-x-1">
          <button
            onClick={rotateLeft}
            className="px-2 py-1 bg-gray-700 text-white rounded"
          >
            <RotateCcw />
          </button>
          <button
            onClick={rotateRight}
            className="px-2 py-1 bg-gray-700 text-white rounded"
          >
            <RotateCw />
          </button>
        </div>
      )}
      {numPages && (
        <div className="flex items-center space-x-4">
          <button
            onClick={goToPreviousPage}
            disabled={currentPage <= 1}
            className={cn(
              'px-3 py-1 bg-gray-700 text-white rounded',
              currentPage <= 1 && 'opacity-50 cursor-not-allowed'
            )}
          >
            Previous
          </button>
          <span className="bg-gray-700 rounded px-2 py-1">
            Page {currentPage} of {numPages}
          </span>
          <button
            onClick={goToNextPage}
            disabled={currentPage >= numPages}
            className={cn(
              'px-3 py-1 bg-gray-700 text-white rounded',
              currentPage >= numPages && 'opacity-50 cursor-not-allowed'
            )}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export const PdfViewer: React.FC<PdfViewerProps> = ({ file }) => {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [scale, setScale] = useState<number>(1.0);
  const [rotation, setRotation] = useState<number>(0);

  const containerRef = useRef<HTMLDivElement>(null);

  const isPdf = file?.contentType === 'application/pdf';

  const onDocumentLoadSuccess = useCallback(
    ({ numPages }: { numPages: number }) => {
      setNumPages(numPages);
      setCurrentPage(1); // Reset to the first page when a new document is loaded
    },
    []
  );
  const rotateLeft = () =>
    setRotation((prevRotation) => (prevRotation - 90) % 360);
  const rotateRight = () =>
    setRotation((prevRotation) => (prevRotation + 90) % 360);

  const zoomIn = () => setScale((prevScale) => Math.min(prevScale + 0.1, 2.0));
  const zoomOut = () => setScale((prevScale) => Math.max(prevScale - 0.1, 0.5));

  const goToNextPage = () =>
    setCurrentPage((prevPage) => Math.min(prevPage + 1, numPages || 1));
  const goToPreviousPage = () =>
    setCurrentPage((prevPage) => Math.max(prevPage - 1, 1));

  const updateScale = useCallback(() => {
    if (containerRef.current) {
      const containerWidth = containerRef.current.clientWidth;
      const idealWidth = 800; // Ideal width for initial scale of 1.0
      const newScale = containerWidth / idealWidth;
      setScale(newScale > 2 ? 2 : newScale < 0.5 ? 0.5 : newScale); // Keep scale between 0.5 and 2.0
    }
  }, []);

  useEffect(() => {
    updateScale(); // Initial update
    window.addEventListener('resize', updateScale);

    return () => {
      window.removeEventListener('resize', updateScale);
    };
  }, [updateScale]);

  return (
    <div className={cn('w-full h-full flex flex-col bg-[#858C8F]')}>
      <PdfHeader
        numPages={isPdf ? numPages : null}
        fileName={file?.title || 'Untitled Document'}
        currentPage={currentPage}
        scale={scale}
        zoomIn={zoomIn}
        zoomOut={zoomOut}
        goToNextPage={goToNextPage}
        goToPreviousPage={goToPreviousPage}
        rotateLeft={rotateLeft}
        rotateRight={rotateRight}
        isPdf={isPdf}
      />
      <div
        ref={containerRef}
        className={cn('flex-1 overflow-auto p-5 bg-[#E2E2E8]')}
        style={{ position: 'relative' }}
      >
        {isPdf ? (
          <Document file={file?.url} onLoadSuccess={onDocumentLoadSuccess}>
            <Page
              key={`page_${currentPage}`}
              pageNumber={currentPage}
              scale={scale}
              renderAnnotationLayer
            />
          </Document>
        ) : (
          <img
            src={file?.url}
            alt={file?.title || 'Image'}
            style={{
              transform: `scale(${scale}) rotate(${rotation}deg)`,
              transformOrigin: 'center center',
              display: 'block',
              margin: '0 auto',
            }}
          />
        )}
        {!isPdf && <div className="mt-4 text-center">{file?.title}</div>}
        {!file?.url && (
          <div className="text-center text-red-500">
            No valid file data provided.
          </div>
        )}
      </div>
    </div>
  );
};
